import type { ChatActionUndo } from '../types/chatStream';
import type {
  ChatActionResult,
  ChatActionStudyStateResult,
} from './chatActions';

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/** Read only the public Undo fields; snapshots/fingerprints are never retained. */
export const readChatActionUndo = (value: unknown): ChatActionUndo | null => {
  if (typeof value !== 'object' || value === null) return null;

  const undo = value as Record<string, unknown>;
  const expiresAt = asString(undo.expiresAt);
  if (typeof undo.available !== 'boolean' || !expiresAt) return null;
  if (!Number.isFinite(Date.parse(expiresAt))) return null;

  const note = asString(undo.note);
  const undoneAt = asString(undo.undoneAt);

  return {
    available: undo.available,
    expiresAt,
    ...(typeof undo.approximate === 'boolean'
      ? { approximate: undo.approximate }
      : {}),
    ...(note ? { note } : {}),
    ...(undoneAt && Number.isFinite(Date.parse(undoneAt)) ? { undoneAt } : {}),
  };
};

/** An action result, or null when this client cannot safely use its shape. */
export const readChatActionResult = (
  value: unknown
): ChatActionResult | null => {
  if (typeof value !== 'object' || value === null) return null;

  const result = value as Record<string, unknown>;

  if (result.kind === 'support_sent') {
    const conversationId = asString(result.conversationId);
    if (!conversationId) return null;

    const url = asString(result.url);

    return {
      kind: 'support_sent',
      topic: asString(result.topic),
      conversationId,
      ...(url ? { url } : {}),
    };
  }

  if (result.kind === 'plan_change' || result.kind === 'study_change') {
    const { undo: rawUndo, ...rest } = result;
    const undo = readChatActionUndo(rawUndo);

    return {
      ...rest,
      kind: result.kind,
      ...(undo ? { undo } : {}),
    } as ChatActionStudyStateResult;
  }

  if (result.kind !== 'open_support') return null;

  const prefill = asString(result.prefill);
  if (!prefill) return null;

  return { kind: 'open_support', topic: asString(result.topic), prefill };
};
